/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react'
import AuthService from '@/services/Auth/AuthService'
import OrderService from '@/services/Order/OrderService'
import { flashStore } from '@/utils/Store'
import { useNavigate } from 'react-router-dom'
import { OrderType } from '@/types/OrderType'
import * as Sentry from '@sentry/react'
import Swal, { SweetAlertResult } from 'sweetalert2'
import { AxiosResponse } from 'axios'

interface Violation {
  propertyPath: string
  message: string
  code: string
}

interface ApiResponse {
  response: AxiosResponse
  violations: Violation[]
}

export default function useOrder() {

     /* Hooks
       *******************************************************************************************/
      const flash = flashStore()
      const navigate = useNavigate()
    
      /* States
       *******************************************************************************************/
      const [orders, setData] = React.useState<OrderType[]>([])
      const [loading, setLoading] = React.useState<boolean>(true)
      const [loadSpinner, setLoadSpinner] = React.useState<boolean>(false)
      const [nextPage, setNextPage] = React.useState<string | undefined>()
      const [prevPage, setPrevPage] = React.useState<string | undefined>()
      const [currentPage, setCurrentPage] = React.useState<number>(1)
      const [perPage, setPerPage] = React.useState<number>(20)
      const [totalItems, setTotalItems] = React.useState<number>(0)
      const [barcode, setBarcode] = React.useState<string>('')
      const [status, setStatus] = React.useState<string>('')
    
      /* Constants
       *******************************************************************************************/
      const perPageOptions = [10, 20, 50, 100]
      const totalPages = Math.ceil(totalItems / perPage)
    
      // Extract query parameters from the URL
      const queryParams = new URLSearchParams(window.location.search)
      const pageParam = Number(queryParams.get('page'))
      const perPageParam = Number(queryParams.get('per_page'))
      const barcodeParam = queryParams.get('barcode')?.toString()
      const statusParam = queryParams.get('status')?.toString()
    
      /* useEffect
       *******************************************************************************************/
      React.useEffect(() => {
        if (pageParam) {
          setCurrentPage(pageParam)
        }
        if (perPageParam) {
          setPerPage(perPageParam)
        }
        if (barcodeParam) {
          setBarcode(barcodeParam)
        }
        if (statusParam) {
          setStatus(statusParam)
        }
        // getOrders()
        getOrders()
      }, [pageParam, perPageParam, barcodeParam, statusParam])
    
      /* Functions
       *******************************************************************************************/
    
      const getOrders = async () => {
        const _currentPage = pageParam ? pageParam : currentPage
        const _perPage = perPageParam ? perPageParam : perPage
        const _barcode = barcodeParam ? barcodeParam : barcode
        const _status = statusParam ? statusParam : status
    
        try {
          const response = await OrderService.getOrders(_currentPage, _perPage, _barcode, _status)
          setData(response.data['hydra:member'])
          setNextPage(response.data['hydra:view']['hydra:next'])
          setPrevPage(response.data['hydra:view']['hydra:previous'])
          setTotalItems(response.data['hydra:totalItems'])
        } catch (error) {
          AuthService.refreshToken()
          Sentry.captureException(error)
        } finally {
          setLoading(false)
          setLoadSpinner(false)
        }
      }
    
      const getPages = (currentPage: number, totalPages: number) => {
        const maxPagesToShow = 5
        let pages = []
    
        if (totalPages <= maxPagesToShow) {
          pages = [...Array(totalPages).keys()].map((x) => x + 1)
        } else {
          if (currentPage <= 3) {
            pages = [1, 2, 3, 4, '...', totalPages]
          } else if (currentPage > totalPages - 3) {
            pages = [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
          } else {
            pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
          }
        }
    
        return pages
      }
    
      const buildQueryString = (
        page: number,
        pageSize: number,
        barcodeValue: string,
        statusValue: string
      ) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('per_page', String(pageSize))
    
        if (barcodeValue) {
          params.set('barcode', barcodeValue)
        }
    
        if (statusValue) {
          params.set('status', statusValue)
        }
    
        return `?${params.toString()}`
      }
    
      const goToPage = (page: number) => {
        setLoadSpinner(true)
        setCurrentPage(page)
        navigate(buildQueryString(page, perPage, barcode, status))
      }
    
      const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPerPage = Number(event.target.value)
        setCurrentPage(1)
        setLoadSpinner(true)
        setPerPage(newPerPage)
        navigate(buildQueryString(1, newPerPage, barcode, status))
      }
    
      const handleUpdateStatus = (id_order: number, status: string) => {
        Swal.fire({
          title: 'Confirmation !',
          text: 'Voulez-vous vraiment modifier le statut de la commande #' + id_order + ' ?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Oui, je confirme',
          cancelButtonText: 'Non',
        }).then((result: SweetAlertResult) => {
          if (result.isConfirmed) {
            OrderService.updateStatus(id_order, status)
              .then(() => {
                flash.setMessage('success', 'Commande #' + id_order + ' modifiée !')
                getOrders()
              })
              .catch((error: ApiResponse) => {
                flash.setMessageErrorForm()
                Sentry.captureException(error)
              })
          }
        })
      }
    
      const handleDelete = (id: number) => {
        Swal.fire({
          title: 'Confirmation !',
          text: 'Voulez-vous vraiment supprimer la commande #' + id + ' ?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Oui, je confirme',
          cancelButtonText: 'Non',
        }).then((result: SweetAlertResult) => {
          if (result.isConfirmed) {
            OrderService._delete(id)
              .then(() => {
                flash.setMessage('success', 'Commande supprimée !')
                getOrders()
              })
              .catch((e: ApiResponse) => {
                if (e?.response?.status == 403) {
                  flash.setMessage('error', e?.response?.data['hydra:description'])
                }
                Sentry.captureException(e)
              })
          }
        })
      }
    
      const handleSendNotification = (id_order: number, key: string) => {
        let nameKey: string = "l'email"
        if (key === 'sms') {
          nameKey = 'le sms'
        }
    
        Swal.fire({
          title: 'Confirmation !',
          text: 'Voulez-vous vraiment renvoyer ' + nameKey + ' de la commande #' + id_order + ' ?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Oui, je confirme',
          cancelButtonText: 'Non',
        }).then((result: SweetAlertResult) => {
          if (result.isConfirmed) {
            if (key === 'sms') {
              sendNotificationOrder(id_order, 'sms')
            } else {
              sendNotificationOrder(id_order, 'mail')
            }
          }
        })
      }
    
      const sendNotificationOrder = (id_order: number, $provider: string) => {
        OrderService.resendNotification(id_order, $provider)
          .then(() => {
            flash.setMessage('success', 'Votre demande a bien été effectuée !')
          })
          .catch((error: ApiResponse) => {
            flash.setMessageErrorForm()
            Sentry.captureException(error)
          })
      }
    
      const handleBarcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBarcode(event.target.value)
      }
    
      const handleFilter = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setLoadSpinner(true)
        setCurrentPage(1)
        getOrders()
        navigate(buildQueryString(1, perPage, barcode, status))
      }
    
      const handleReset = () => {
        setStatus('')
        setBarcode('')
        setCurrentPage(1)
        setLoadSpinner(false)
        navigate(buildQueryString(1, perPage, '', ''))
      }
    
    
    return {
      barcode,
      status,
      setStatus,
      orders,
      loading,
      loadSpinner,
      nextPage,
      prevPage,
      perPageOptions,
      totalPages,
      currentPage,
      perPage,
      totalItems,
      getPages,
      goToPage,
      handlePerPageChange,
      handleUpdateStatus,
      handleDelete,
      handleSendNotification,
      handleBarcodeChange,
      handleFilter,
      handleReset,
    }
}